import { ref, get, update } from 'firebase/database';
import { database } from '../firebase/config';
import { Empresa, ClienteResumo, Assinatura, OnboardingData, DashboardMetrics } from '../types';

class MasterFirebaseService {

  async getTodasEmpresas(): Promise<Empresa[]> {
    const snapshot = await get(ref(database, 'empresas'));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const empresas: Empresa[] = [];

    Object.keys(data).forEach((cnpj) => {
      const emp = data[cnpj];
      if (emp && emp.nomeFantasia) {
        empresas.push({
          cnpj,
          nomeFantasia: emp.nomeFantasia || '',
          razaoSocial: emp.razaoSocial || '',
          email: emp.email || '',
          telefone: emp.telefone || '',
          site: emp.site || '',
          status: emp.status !== false,
          createDate: emp.createDate || '',
          updateDate: emp.updateDate || '',
          deleteDate: emp.deleteDate || '',
          inscricaoEstadual: emp.inscricaoEstadual || '',
          numero: emp.numero || '',
          complemento: emp.complemento || '',
          bairro: emp.bairro || '',
          cidade: emp.cidade || '',
          logradouro: emp.logradouro || '',
          cep: emp.cep || '',
          googlePlaceId: emp.googlePlaceId || '',
          logoUrl: emp.logoUrl || '',
          plano: emp.plano || undefined,
          asaasConfig: emp.asaasConfig || undefined,
        });
      }
    });

    return empresas;
  }

  async getClientesResumo(): Promise<ClienteResumo[]> {
    const [empresasSnap, assinaturasSnap, mensalidadesSnap] = await Promise.all([
      get(ref(database, 'empresas')),
      get(ref(database, 'assinaturas')),
      get(ref(database, 'mensalidades')),
    ]);

    if (!empresasSnap.exists()) return [];

    const data = empresasSnap.val();
    const assinaturasRaiz = assinaturasSnap.exists() ? assinaturasSnap.val() : {};
    const mensalidadesRaiz = mensalidadesSnap.exists() ? mensalidadesSnap.val() : {};
    const clientes: ClienteResumo[] = [];

    for (const cnpj of Object.keys(data)) {
      const emp = data[cnpj];
      if (!emp || !emp.nomeFantasia) continue;

      const usuarios = emp.usuarios ? Object.keys(emp.usuarios).length : 0;
      const alunos = emp.alunos ? Object.keys(emp.alunos).filter(k => {
        const a = emp.alunos[k];
        return a && a.bAtivo !== false;
      }).length : 0;

      const temAssinaturaAtiva = this.verificarAssinaturaAtiva(cnpj, assinaturasRaiz);
      const temPlanoAtivo = emp.plano && emp.plano.status === 'ativo';

      let statusFinanceiro: 'em_dia' | 'atraso' | 'sem_assinatura' = 'sem_assinatura';

      if (temAssinaturaAtiva || temPlanoAtivo) {
        statusFinanceiro = 'em_dia';

        if (this.verificarAtraso(cnpj, assinaturasRaiz, mensalidadesRaiz)) {
          statusFinanceiro = 'atraso';
        }
      }

      let planoValor = emp.plano?.valor || 0;
      if (!planoValor && assinaturasRaiz[cnpj]) {
        const subs = Object.values(assinaturasRaiz[cnpj]) as any[];
        const ativa = subs.find(a => a.status === 'ACTIVE');
        if (ativa) planoValor = ativa.valor ?? ativa.value ?? 0;
      }

      clientes.push({
        cnpj,
        nomeFantasia: emp.nomeFantasia || '',
        email: emp.email || '',
        telefone: emp.telefone || '',
        status: emp.status !== false,
        createDate: emp.createDate || '',
        planoNome: emp.plano?.nome || (temAssinaturaAtiva ? 'Assinatura ativa' : 'Sem plano'),
        planoValor,
        planoStatus: emp.plano?.status || (temAssinaturaAtiva ? 'ativo' : 'inativo'),
        totalAlunos: alunos,
        totalUsuarios: usuarios,
        statusFinanceiro,
      });
    }

    return clientes.sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia));
  }

  private verificarAssinaturaAtiva(cnpj: string, assinaturasRaiz: any): boolean {
    if (assinaturasRaiz[cnpj]) {
      const subs = Object.values(assinaturasRaiz[cnpj]) as any[];
      if (subs.some(a => a.status === 'ACTIVE')) return true;
    }
    return false;
  }

  private verificarAtraso(cnpj: string, assinaturasRaiz: any, mensalidadesRaiz: any): boolean {
    if (assinaturasRaiz[cnpj]) {
      const subs = Object.values(assinaturasRaiz[cnpj]) as any[];
      if (subs.some(a => a.status === 'OVERDUE' || a.status === 'PAST_DUE')) return true;
    }

    if (mensalidadesRaiz[cnpj]) {
      const hoje = new Date();
      const empresaMensalidades = mensalidadesRaiz[cnpj];
      for (const chave of Object.keys(empresaMensalidades)) {
        const item = empresaMensalidades[chave];
        if (item && typeof item === 'object') {
          const entries = Object.values(item) as any[];
          if (entries.some(m => {
            if (m.status === 'pendente' || m.status === 'vencida' || m.status === 'PENDING' || m.status === 'OVERDUE') {
              const venc = m.dataVencimento || m.dueDate;
              return venc && new Date(venc) < hoje;
            }
            return false;
          })) return true;
        }
      }
    }

    return false;
  }

  async getEmpresaDetalhes(cnpj: string): Promise<Empresa | null> {
    const snapshot = await get(ref(database, `empresas/${cnpj}`));
    if (!snapshot.exists()) return null;

    const emp = snapshot.val();
    return {
      cnpj,
      nomeFantasia: emp.nomeFantasia || '',
      razaoSocial: emp.razaoSocial || '',
      email: emp.email || '',
      telefone: emp.telefone || '',
      site: emp.site || '',
      status: emp.status !== false,
      createDate: emp.createDate || '',
      updateDate: emp.updateDate || '',
      deleteDate: emp.deleteDate || '',
      inscricaoEstadual: emp.inscricaoEstadual || '',
      numero: emp.numero || '',
      complemento: emp.complemento || '',
      bairro: emp.bairro || '',
      cidade: emp.cidade || '',
      logradouro: emp.logradouro || '',
      cep: emp.cep || '',
      googlePlaceId: emp.googlePlaceId || '',
      logoUrl: emp.logoUrl || '',
      plano: emp.plano || undefined,
      asaasConfig: emp.asaasConfig || undefined,
    };
  }

  async getAssinaturasEmpresa(cnpj: string): Promise<Assinatura[]> {
    const snapshot = await get(ref(database, `assinaturas/${cnpj}`));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const resultado: Assinatura[] = [];

    Object.keys(data).forEach(key => {
      const a = data[key];
      if (!a) return;
      resultado.push({
        id: key,
        asaasSubscriptionId: a.asaasSubscriptionId || '',
        customerId: a.customerId || '',
        status: a.status || '',
        cycle: a.ciclo || a.cycle || '',
        value: a.valor ?? a.value ?? 0,
        description: a.descricao || a.description || '',
        nextDueDate: a.proximoVencimento || a.nextDueDate || '',
        createDate: a.dataCriacao || a.createDate || '',
        updateDate: a.dataAtualizacao || a.updateDate || '',
        empresaId: a.empresaId || cnpj,
        codigo: a.codigo || '',
        dataInicio: a.dataInicio || '',
        origem: 'legacy',
      });
    });

    return resultado;
  }

  async getContagens(cnpj: string): Promise<{ alunos: number; alunosAtivos: number; usuarios: number }> {
    const [alunosSnap, usuariosSnap] = await Promise.all([
      get(ref(database, `empresas/${cnpj}/alunos`)),
      get(ref(database, `empresas/${cnpj}/usuarios`)),
    ]);

    let alunos = 0;
    let alunosAtivos = 0;
    if (alunosSnap.exists()) {
      const data = alunosSnap.val();
      Object.values(data).forEach((a: any) => {
        alunos++;
        if (a.bAtivo !== false) alunosAtivos++;
      });
    }

    const usuarios = usuariosSnap.exists() ? Object.keys(usuariosSnap.val()).length : 0;

    return { alunos, alunosAtivos, usuarios };
  }

  async getMensalidadesEmpresa(cnpj: string): Promise<any[]> {
    const snapshot = await get(ref(database, `mensalidades/${cnpj}`));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const mensalidades: any[] = [];
    this.extrairMensalidades(data, mensalidades, '');
    return mensalidades;
  }

  private extrairMensalidades(node: any, resultado: any[], path: string): void {
    if (!node || typeof node !== 'object') return;

    if (node.valor !== undefined || node.value !== undefined || node.dataVencimento || node.dueDate || node.status) {
      resultado.push({
        ...node,
        id: node.id || path,
        grupoId: path,
      });
      return;
    }

    Object.keys(node).forEach(key => {
      this.extrairMensalidades(node[key], resultado, path ? `${path}/${key}` : key);
    });
  }

  async alterarStatusEmpresa(cnpj: string, novoStatus: boolean): Promise<void> {
    await update(ref(database, `empresas/${cnpj}`), {
      status: novoStatus,
      updateDate: new Date().toISOString(),
    });
  }

  async getOnboardingsPendentes(): Promise<OnboardingData[]> {
    const snapshot = await get(ref(database, 'onboarding'));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const pendentes: OnboardingData[] = [];

    Object.keys(data).forEach(uid => {
      const ob = data[uid];
      if (ob && ob.status !== 'concluido') {
        pendentes.push({
          nome: ob.nome || '',
          email: ob.email || '',
          telefone: ob.telefone || '',
          cnpj: ob.cnpj || '',
          empresaNome: ob.empresaNome || '',
          status: ob.status || 'pendente',
          etapaAtual: ob.etapaAtual || 1,
          planoSelecionado: ob.planoSelecionado,
          planoNome: ob.planoNome,
          planoValor: ob.planoValor,
          dataCriacao: ob.dataCriacao || ob.createDate || '',
          dataUltimaAtividade: ob.dataUltimaAtividade || '',
        });
      }
    });

    return pendentes.sort((a, b) =>
      new Date(b.dataUltimaAtividade || 0).getTime() - new Date(a.dataUltimaAtividade || 0).getTime()
    );
  }

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const clientes = await this.getClientesResumo();

    const clientesAtivos = clientes.filter(c => c.status).length;
    const clientesInativos = clientes.filter(c => !c.status).length;
    const clientesEmAtraso = clientes.filter(c => c.statusFinanceiro === 'atraso').length;
    const receitaMensal = clientes
      .filter(c => c.status && c.planoStatus === 'ativo')
      .reduce((sum, c) => sum + c.planoValor, 0);

    const onboardings = await this.getOnboardingsPendentes();

    return {
      totalClientes: clientes.length,
      clientesAtivos,
      clientesInativos,
      clientesEmAtraso,
      receitaMensal,
      onboardingsPendentes: onboardings.length,
    };
  }
}

export const masterFirebaseService = new MasterFirebaseService();
